import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Download, Plus, AlertTriangle, CheckCircle, XCircle, X,
  Users, ShieldOff, Zap, CreditCard,
  Loader2, RefreshCw, Eye, FileText, ChevronLeft, ChevronRight,
  Bot, Coins, Wallet, Calendar,
} from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import api from '../../utils/api';
import CustomDropdown from '../CustomDropdown';

// ─── helpers ──────────────────────────────────────────────────────────────────
function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}
function fmt(n) { return Number(n).toLocaleString('en-IN'); }
function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Plan configs ─────────────────────────────────────────────────────────────
const PLAN_CONFIGS = {
  starter: {
    id: 'starter', name: 'Starter', price: 999, tagline: 'Perfect for small teams getting started',
    maxAiAgents: 3, maxHumanAgents: 3, maxUsers: 3, creditsTotal: 5000,
    features: [
      { label: '3 AI agents', included: true },
      { label: '3 Human agents', included: true },
      { label: '3 Users limit', included: true },
      { label: '5,000 credits/mo', included: true },
      { label: 'WhatsApp + web', included: true },
      { label: 'Email support', included: true },
      { label: 'RBAC / SSO', included: false },
    ],
  },
  growth: {
    id: 'growth', name: 'Growth', price: 2499, tagline: 'For growing businesses and teams',
    maxAiAgents: 5, maxHumanAgents: 5, maxUsers: 5, creditsTotal: 25000,
    features: [
      { label: '5 AI agents', included: true },
      { label: '5 Human agents', included: true },
      { label: '5 Users limit', included: true },
      { label: '25,000 credits/mo', included: true },
      { label: 'WhatsApp + web', included: true },
      { label: 'Email support', included: true },
      { label: 'Priority support', included: true },
    ],
  },
  professional: {
    id: 'professional', name: 'Professional', price: 4999, tagline: 'For power users and scaling teams',
    maxAiAgents: 7, maxHumanAgents: 7, maxUsers: 7, creditsTotal: 75000,
    features: [
      { label: '7 AI agents', included: true },
      { label: '7 Human agents', included: true },
      { label: '7 Users limit', included: true },
      { label: '75,000 credits/mo', included: true },
      { label: 'All channels', included: true },
      { label: 'Priority support', included: true },
      { label: 'Basic analytics', included: true },
    ],
  },
  enterprise: {
    id: 'enterprise', name: 'Enterprise', price: 'Custom', tagline: 'For large enterprises and organizations',
    maxAiAgents: 999, maxHumanAgents: 999, maxUsers: 999, creditsTotal: 999999,
    features: [
      { label: 'Unlimited AI agents', included: true },
      { label: 'Unlimited Human agents', included: true },
      { label: 'Unlimited Users limit', included: true },
      { label: 'Custom credits', included: true },
      { label: 'RBAC', included: true },
      { label: 'SLA + dedicated CSM', included: true },
      { label: 'Custom contract', included: true },
    ],
  },
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ─── sub-components ───────────────────────────────────────────────────────────
function StatCard({ icon: Icon, image, label, value, sub, highlight, loading }) {
  return (
    <div className={`w-full bg-white p-3.5 sm:p-5 rounded-xl sm:rounded-2xl border border-zinc-200/80 shadow-2xs hover:shadow-xs transition-all duration-300 flex items-center justify-between min-h-[110px] sm:min-h-[130px] relative overflow-hidden group
      ${highlight ? 'border-amber-300 bg-amber-50/50' : ''}`}
    >
      <div className="flex flex-col justify-between min-h-[85px] sm:min-h-[102px] flex-1 min-w-0">
        <span className="text-xs font-medium text-zinc-500">{label}</span>
        <div>
          {loading ? (
            <div className="h-7 sm:h-8 w-20 bg-zinc-100 animate-pulse rounded mt-1.5" />
          ) : (
            <span className="text-lg sm:text-xl font-semibold text-zinc-900 tracking-tight block break-words">{value}</span>
          )}
        </div>
        {sub && (
          typeof sub === 'string' ? (
            <p className="text-[10px] font-semibold text-zinc-400 mt-1 truncate">{sub}</p>
          ) : (
            <div className="text-[10px] font-semibold mt-1">{sub}</div>
          )
        )}
      </div>
      {(Icon || image) && (
        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-zinc-50 border border-zinc-200/80 flex items-center justify-center shrink-0 ml-2 text-zinc-700">
          {Icon ? <Icon size={20} strokeWidth={1.8} /> : <img src={image} className="w-full h-full object-cover scale-110" alt="" />}
        </div>
      )}
    </div>
  );
}

function ProgressBar({ used, total, type = 'consumed' }) {
  let pct = 0;
  let isAlertOnLow = false;

  if (type === 'remaining') {
    pct = total > 0 ? (used / total) * 100 : 0;
    isAlertOnLow = true;
  } else if (type === 'consumed_remaining') {
    pct = total > 0 ? ((total - used) / total) * 100 : 0;
    isAlertOnLow = true;
  } else {
    pct = total > 0 ? (used / total) * 100 : 0;
    isAlertOnLow = false;
  }
  
  pct = Math.min(100, Math.max(0, pct));

  let barBg = 'bg-zinc-900';

  if (isAlertOnLow) {
    if (pct <= 15) {
      barBg = 'bg-rose-500';
    } else if (pct <= 35) {
      barBg = 'bg-amber-500';
    } else {
      barBg = 'bg-zinc-900';
    }
  } else {
    if (pct >= 85) {
      barBg = 'bg-rose-500';
    } else if (pct >= 65) {
      barBg = 'bg-amber-500';
    } else {
      barBg = 'bg-zinc-900';
    }
  }

  const displayWidth = (isAlertOnLow && pct === 0) ? 2 : pct;

  return (
    <div className="w-full space-y-1.5">
      <div className="w-full bg-zinc-100 rounded-full h-1.5 overflow-hidden relative">
        <div
          className={`h-full rounded-full transition-all duration-300 ${barBg}`}
          style={{ width: `${displayWidth}%` }}
        />
      </div>
    </div>
  );
}


// ─── User Slot Row — view only (no edit/delete) ───────────────────────────────
function UserSlotRow({ slot }) {
  const roleColor = {
    Admin: 'bg-zinc-900 text-white',
    Manager: 'bg-zinc-100 text-zinc-800 border border-zinc-200',
    Agent: 'bg-zinc-100 text-zinc-800 border border-zinc-200',
    Viewer: 'bg-zinc-50 text-zinc-500 border border-zinc-200',
  }[slot.role] || 'bg-zinc-50 text-zinc-500';

  return (
    <div className="flex items-center gap-3 py-2.5 px-3.5 bg-zinc-50/60 rounded-xl border border-zinc-200/80">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-zinc-200 text-zinc-800 font-bold text-xs">
        {slot.name ? slot.name.charAt(0).toUpperCase() : '?'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-zinc-900 truncate">{slot.name || '—'}</p>
        <p className="text-[11px] text-zinc-400 truncate">{slot.email || '—'}</p>
      </div>
      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md flex-shrink-0 ${roleColor}`}>
        {slot.role}
      </span>
    </div>
  );
}

// ─── Agent Slot — view only (no edit/delete) ──────────────────────────────────
function AgentSlot({ agent, index, onAdd, disabled }) {
  if (!agent) {
    return (
      <button
        disabled={disabled}
        onClick={onAdd}
        className="flex items-center gap-2.5 py-2.5 px-3.5 border border-dashed border-zinc-200 rounded-xl text-zinc-400
          hover:border-zinc-900 hover:text-zinc-900 cursor-pointer transition-all group w-full disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus size={14} className="group-hover:scale-110 transition-transform" />
        <span className="text-xs font-medium">Empty slot — create an agent</span>
      </button>
    );
  }

  const statusColor = {
    Active: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    Inactive: 'bg-zinc-100 text-zinc-500 border border-zinc-200',
    Email: 'bg-blue-50 text-blue-700 border border-blue-200',
    WhatsApp: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    Web: 'bg-purple-50 text-purple-700 border border-purple-200',
  }[agent.status] || 'bg-zinc-100 text-zinc-500';

  return (
    <div className="flex items-center gap-3 py-2.5 px-3.5 bg-zinc-50/60 rounded-xl border border-zinc-200/80">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-zinc-900 truncate">{agent.name}</p>
        <p className="text-[11px] text-zinc-400">{agent.totalSessions ?? 0} sessions</p>
      </div>
      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${statusColor}`}>
        {agent.status}
      </span>
    </div>
  );
}

// ─── Plan Confirm Modal ──────────────────────────────────────────────────────
const ADDON_PRICE_PER_AGENT = 900;

function PlanConfirmModal({ plan, billingCycle, onConfirm, onClose, loading, isCurrent }) {
  const isEnterprise = plan.price === 'Custom';
  const basePrice = isCurrent ? 0 : (isEnterprise ? 0 : (billingCycle === 'yearly' ? Math.round(plan.price * 0.8) * 12 : plan.price));
  const [extraAiAgents, setExtraAiAgents] = useState(0);
  const [extraHumanAgents, setExtraHumanAgents] = useState(0);
  const [extraCredits, setExtraCredits] = useState(null); // pack object or null

  const TOPUP_PACKS = [
    { k: '25K', price: 499 },
    { k: '100K', price: 1499 },
    { k: '500K', price: 3999 },
  ];

  const addonCost = (extraAiAgents + extraHumanAgents) * ADDON_PRICE_PER_AGENT + (extraCredits?.price ?? 0);
  const subtotal = basePrice + addonCost;
  const gst = Math.round(subtotal * 0.18);
  const total = subtotal + gst;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-3 sm:p-4 md:p-6 font-['Inter',sans-serif] animate-fadeIn">
      <div className="w-full max-w-2xl bg-white rounded-2xl border border-zinc-200/90 shadow-2xl shadow-zinc-900/20 flex flex-col max-h-[92vh] overflow-hidden animate-slideIn">
        {/* Header */}
        <div className="flex items-center justify-between p-5 sm:p-6 pb-3 sm:pb-4 border-b border-zinc-100 flex-shrink-0">
          <div className="min-w-0 pr-2">
            <h3 className="text-sm sm:text-base font-semibold text-zinc-900 tracking-tight truncate">{isCurrent ? `${plan.name} Add-ons` : `${plan.name} Plan`}</h3>
            <p className="text-xs text-zinc-500 mt-0.5 truncate">{plan.tagline}</p>
          </div>
          <button 
            onClick={onClose} 
            className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors shrink-0"
            aria-label="Close dialog"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable Checkout Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 py-4 sm:py-6 space-y-4 sm:space-y-6 custom-scrollbar">
          {/* Plan features */}
          {!isCurrent && (
            <div className="bg-zinc-50/70 rounded-xl p-4 border border-zinc-200/60">
              <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-2.5">What's included</p>
              <ul className="space-y-2">
                {plan.features.map((f, i) => (
                  <li key={i} className={`flex items-center gap-2 text-xs ${f.included ? 'text-zinc-800' : 'text-zinc-400'}`}>
                    {f.included
                      ? <CheckCircle size={13} className="text-zinc-900 flex-shrink-0" />
                      : <XCircle size={13} className="text-zinc-300 flex-shrink-0" />}
                    <span className="font-medium text-[11px] sm:text-xs">{f.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Add-ons */}
          <div>
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-2.5">Add-ons <span className="normal-case font-normal text-zinc-500">(₹{fmt(ADDON_PRICE_PER_AGENT)}/agent)</span></p>
            <div className="space-y-2.5">
              {/* Extra AI Agents */}
              <div className="flex items-center justify-between bg-zinc-50/70 border border-zinc-200/60 rounded-xl px-3.5 py-2.5">
                <div>
                  <p className="text-xs font-semibold text-zinc-900">Extra AI Agents</p>
                  <p className="text-[10px] text-zinc-400">₹{fmt(ADDON_PRICE_PER_AGENT)} each</p>
                </div>
                <div className="flex items-center gap-2.5">
                  <button onClick={() => setExtraAiAgents(n => Math.max(0, n - 1))} className="w-6 h-6 rounded-lg bg-zinc-200 hover:bg-zinc-300 text-zinc-700 font-bold text-xs flex items-center justify-center transition-all">−</button>
                  <span className="text-xs font-bold text-zinc-900 w-5 text-center">{extraAiAgents}</span>
                  <button onClick={() => setExtraAiAgents(n => n + 1)} className="w-6 h-6 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs flex items-center justify-center transition-all">+</button>
                </div>
              </div>
              {/* Extra Human Agents */}
              <div className="flex items-center justify-between bg-zinc-50/70 border border-zinc-200/60 rounded-xl px-3.5 py-2.5">
                <div>
                  <p className="text-xs font-semibold text-zinc-900">Extra Human Agents</p>
                  <p className="text-[10px] text-zinc-400">₹{fmt(ADDON_PRICE_PER_AGENT)} each</p>
                </div>
                <div className="flex items-center gap-2.5">
                  <button onClick={() => setExtraHumanAgents(n => Math.max(0, n - 1))} className="w-6 h-6 rounded-lg bg-zinc-200 hover:bg-zinc-300 text-zinc-700 font-bold text-xs flex items-center justify-center transition-all">−</button>
                  <span className="text-xs font-bold text-zinc-900 w-5 text-center">{extraHumanAgents}</span>
                  <button onClick={() => setExtraHumanAgents(n => n + 1)} className="w-6 h-6 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs flex items-center justify-center transition-all">+</button>
                </div>
              </div>
              {/* Credit top-up */}
              <div className="bg-zinc-50/70 border border-zinc-200/60 rounded-xl px-3.5 py-2.5">
                <p className="text-xs font-semibold text-zinc-900 mb-2">Credit Top-up Pack</p>
                <div className="flex gap-2">
                  {TOPUP_PACKS.map(pack => (
                    <button
                      key={pack.k}
                      onClick={() => setExtraCredits(extraCredits?.k === pack.k ? null : pack)}
                      className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${extraCredits?.k === pack.k
                        ? 'border-zinc-900 bg-zinc-900 text-white shadow-xs'
                        : 'border-zinc-200 text-zinc-700 bg-white hover:border-zinc-300'
                        }`}
                    >
                      <div>{pack.k}</div>
                      <div className={`text-[10px] font-normal ${extraCredits?.k === pack.k ? 'text-zinc-300' : 'text-zinc-400'}`}>₹{fmt(pack.price)}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Price breakdown */}
          <div className="border-t border-zinc-100 pt-3.5 space-y-1.5">
            {!isCurrent && (
              <div className="flex justify-between text-xs text-zinc-500">
                <span>Base plan ({billingCycle})</span>
                <span className="font-medium text-zinc-800">₹{fmt(basePrice)}</span>
              </div>
            )}
            {extraAiAgents > 0 && (
              <div className="flex justify-between text-xs text-zinc-500 pl-2.5 border-l-2 border-zinc-300">
                <span>+ Extra AI Agents ({extraAiAgents})</span>
                <span className="font-medium text-zinc-800">₹{fmt(extraAiAgents * ADDON_PRICE_PER_AGENT)}</span>
              </div>
            )}
            {extraHumanAgents > 0 && (
              <div className="flex justify-between text-xs text-zinc-500 pl-2.5 border-l-2 border-zinc-300">
                <span>+ Extra Human Agents ({extraHumanAgents})</span>
                <span className="font-medium text-zinc-800">₹{fmt(extraHumanAgents * ADDON_PRICE_PER_AGENT)}</span>
              </div>
            )}
            {extraCredits && (
              <div className="flex justify-between text-xs text-zinc-500 pl-2.5 border-l-2 border-zinc-300">
                <span>+ Credit Pack ({extraCredits.k})</span>
                <span className="font-medium text-zinc-800">₹{fmt(extraCredits.price)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs text-zinc-500">
              <span>GST (18%)</span>
              <span className="font-medium text-zinc-800">₹{fmt(gst)}</span>
            </div>
            <div className="flex justify-between text-sm font-bold text-zinc-900 pt-2 border-t border-zinc-100">
              <span>Total</span>
              <span>₹{fmt(total)}</span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3 p-4 sm:p-6 md:p-8 pt-3 sm:pt-4 border-t border-zinc-100 flex-shrink-0">
          <button onClick={onClose} className="w-full sm:w-auto px-5 py-2.5 text-xs font-semibold text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-xl hover:bg-zinc-100 hover:text-zinc-900 transition-all active:scale-[0.99]">Cancel</button>
          <button
            onClick={() => onConfirm({ extraAiAgents, extraHumanAgents, extraCredits, total })}
            disabled={loading || (isCurrent && extraAiAgents === 0 && extraHumanAgents === 0 && !extraCredits)}
            className="w-full sm:flex-1 py-2.5 rounded-xl bg-zinc-900 text-white text-xs font-semibold hover:bg-zinc-800 transition-all disabled:opacity-40 flex items-center justify-center gap-2 shadow-xs active:scale-[0.99]"
          >
            {loading ? <><Loader2 size={13} className="animate-spin" /> Processing…</> : <><CreditCard size={13} /> Proceed to Pay</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Plan Card ──────────────────────────────────────────────────────────────
function PlanCard({ plan, isCurrent, billingCycle, onOpenModal, loading, serviceActive }) {
  const monthlyDiscountedPrice =
    billingCycle === 'yearly' && plan.price !== 'Custom'
      ? Math.round(plan.price * 0.8)
      : plan.price;
  const isEnterprise = plan.price === 'Custom';

  return (
    <div className={`relative rounded-2xl border p-4 sm:p-5 flex flex-col gap-3 sm:gap-4 transition-all duration-200 ${isCurrent
      ? 'border-zinc-900 bg-zinc-50/40 shadow-sm'
      : 'border-zinc-200/80 bg-white hover:border-zinc-300 hover:shadow-xs'
      }`}>
      {isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-zinc-900 text-white text-[10px] font-semibold uppercase tracking-wider px-3 py-0.5 rounded-full shadow-xs whitespace-nowrap">
            Current plan
          </span>
        </div>
      )}
      <div>
        <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">{plan.name}</h4>
        {billingCycle === 'yearly' && !isEnterprise ? (
          <div className="mt-1.5">
            <p className="text-xl sm:text-2xl font-bold text-zinc-900 tracking-tight leading-tight">
              ₹{fmt(monthlyDiscountedPrice)}
              <span className="text-xs font-normal text-zinc-400 lowercase">/mo</span>
            </p>
            <p className="text-[10px] text-emerald-600 font-semibold tracking-wide mt-0.5">
              Billed ₹{fmt(monthlyDiscountedPrice * 12)}/yr · Save 20%
            </p>
          </div>
        ) : (
          <p className="text-xl sm:text-2xl font-bold text-zinc-900 tracking-tight mt-1.5">
            {isEnterprise ? 'Custom' : `₹${fmt(plan.price)}`}
            {!isEnterprise && <span className="text-xs font-normal text-zinc-400 lowercase">/mo</span>}
          </p>
        )}
        <p className="text-xs text-zinc-500 mt-1 font-normal leading-relaxed">{plan.tagline}</p>
      </div>
      <div className="border-t border-zinc-100 my-0.5"></div>
      <ul className="space-y-2 text-xs text-zinc-600 flex-1">
        {plan.features.map((f, i) => (
          <li key={i} className={`flex items-center gap-2 ${f.included ? '' : 'opacity-35'}`}>
            {f.included
              ? <CheckCircle size={13} className="text-zinc-900 flex-shrink-0" />
              : <XCircle size={13} className="text-zinc-300 flex-shrink-0" />}
            <span className="font-medium text-[11px] sm:text-xs">{f.label}</span>
          </li>
        ))}
      </ul>
      {isCurrent ? (
        !isEnterprise && (
          <>
            <div className="border-t border-zinc-100 my-0.5"></div>
            <button
              onClick={() => onOpenModal(plan)}
              className="w-full py-2.5 rounded-xl bg-zinc-900 text-white text-xs font-semibold hover:bg-zinc-800 transition-all flex items-center justify-center gap-2 shadow-xs active:scale-[0.99]"
            >
              {loading ? <><Loader2 size={13} className="animate-spin" /> Processing…</> : (serviceActive ? 'Add-ons' : 'Renew Plan')}
            </button>
          </>
        )
      ) : (
        <>
          <div className="border-t border-zinc-100 my-0.5"></div>
          {isEnterprise ? (
            <button
              onClick={() => window.open('mailto:contact@kambaa.in', '_blank')}
              className="w-full py-2.5 rounded-xl border border-zinc-200 text-zinc-800 text-xs font-semibold hover:border-zinc-900 hover:text-zinc-900 transition-all bg-white shadow-xs active:scale-[0.99]"
            >
              Contact sales ↗
            </button>
          ) : (
            <button
              disabled={loading}
              onClick={() => onOpenModal(plan)}
              className="w-full py-2.5 rounded-xl bg-zinc-50 hover:bg-zinc-900 text-zinc-700 hover:text-white text-xs font-semibold transition-all disabled:opacity-40 flex items-center justify-center gap-2 border border-zinc-200 hover:border-zinc-900 active:scale-[0.99]"
            >
              {loading ? <><Loader2 size={13} className="animate-spin" /> Processing…</> : 'Switch Plan'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function BillingDashboard({
  userName, subscription,
  onSubscriptionPay, onCreditPay, loadingId = '',
  paymentHistory = [], serverUrl,
}) {
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const envApiUrl = process.env.REACT_APP_API_BASE_URL || '';
  const SERVER_URL = serverUrl || (envApiUrl ? envApiUrl.replace('/api', '') : '');

  const [billingCycle, setBillingCycle] = useState('monthly');
  const [activeSubTab, setActiveSubTab] = useState('overview'); // 'overview' or 'invoices'

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'invoices') {
      setActiveSubTab('invoices');
    } else {
      setActiveSubTab('overview');
    }
  }, [location.search]);

  const [serviceActive, setServiceActive] = useState(true);
  const [agents, setAgents] = useState([]);
  const [agentPage, setAgentPage] = useState(1);
  const [confirmPlan, setConfirmPlan] = useState(null); // plan object to show in modal

  // ── Daily Token Limit States & Hooks ─────────────────────────────────────────
  const [dailyTokenLimit, setDailyTokenLimit] = useState(50000);
  const [savingLimit, setSavingLimit] = useState(false);

  // Fetch limit on load
  useEffect(() => {
    if (!userName) return;
    const userStr = sessionStorage.getItem('user');
    const userObj = userStr ? JSON.parse(userStr) : null;
    const tenantId = userObj?.tenant_id || userName;

    api.get(`/billing/token-limit/${tenantId}`)
      .then(d => {
        if (d && d.success && d.daily_token_limit !== undefined) {
          setDailyTokenLimit(d.daily_token_limit);
        }
      })
      .catch(err => console.error('Fetch daily token limit error:', err));
  }, [userName]);

  // Save limit
  const handleSaveTokenLimit = async () => {
    const userStr = sessionStorage.getItem('user');
    const userObj = userStr ? JSON.parse(userStr) : null;
    const tenantId = userObj?.tenant_id || userName;

    setSavingLimit(true);
    try {
      await api.put(`/billing/token-limit/${tenantId}`, { limit: dailyTokenLimit });
      showToast('Daily token limit updated successfully!', 'success');
    } catch (err) {
      console.error('Save daily token limit error:', err);
      showToast(err.message || 'Could not save limit', 'error');
    } finally {
      setSavingLimit(false);
    }
  };

  // ── Today's Usage — live tokens used today vs daily budget limit ─────────────
  const [todayUsage, setTodayUsage] = useState({ tokens_today: 0, daily_token_limit: 50000, pct_used: 0 });
  const [todayUsageLoading, setTodayUsageLoading] = useState(true);

  useEffect(() => {
    if (!userName) return;
    const userStr = sessionStorage.getItem('user');
    const userObj = userStr ? JSON.parse(userStr) : null;
    const tenantId = userObj?.tenant_id || userName;

    const fetchTodayUsage = () => {
      setTodayUsageLoading(true);
      api.get(`/credits/today/${tenantId}`)
        .then(d => {
          if (d && d.success && d.data) setTodayUsage(d.data);
        })
        .catch(err => console.error('Fetch today usage error:', err))
        .finally(() => setTodayUsageLoading(false));
    };

    fetchTodayUsage();
    // Refresh every 60s so the card stays live
    const interval = setInterval(fetchTodayUsage, 60000);
    return () => clearInterval(interval);
  }, [userName, SERVER_URL]);

  // ── Invoice filter state ─────────────────────────────────────────────────────
  const [invoiceFilterYear, setInvoiceFilterYear] = useState('');
  const [invoiceFilterMonth, setInvoiceFilterMonth] = useState('');

  // ── User slot state ──────────────────────────────────────────────────────────
  const [userSlots, setUserSlots] = useState([]);
  const [userSlotsLoading, setUserSlotsLoading] = useState(false);
  const [userSlotsError, setUserSlotsError] = useState(null);

  const TOPUP_PACKS = [
    { k: '25K', price: 499 },
    { k: '100K', price: 1499, popular: true },
    { k: '500K', price: 3999 },
  ];
  const [selectedPack, setSelectedPack] = useState(TOPUP_PACKS[0]);

  // Resolve plan config
  const resolvePlanConfig = (sub) => {
    if (!sub) return null;
    const byId = PLAN_CONFIGS[sub.planId];
    if (byId) return byId;
    const byName = PLAN_CONFIGS[sub.planName?.toLowerCase()];
    if (byName) return byName;
    for (const key of Object.keys(PLAN_CONFIGS))
      if (sub.planName?.toLowerCase().includes(key)) return PLAN_CONFIGS[key];
    return null;
  };

  const planConfig = resolvePlanConfig(subscription);

  // ── Load agents ──────────────────────────────────────────────────────────────
  const userStr = sessionStorage.getItem('user');
  const userObj = userStr ? JSON.parse(userStr) : null;
  const tenantId = userObj?.tenant_id || userName;
  const totalSlots = subscription?.maxAiAgents ?? subscription?.maxAgents ?? 0;

  useEffect(() => {
    if (!userName) return;

    api.get(`/billing/ai-agents/${tenantId}`)
      .then(d => {
        const loaded = (d && d.success && Array.isArray(d.data)) ? d.data.map(a => ({
          name: a.agent_name,
          status: a.status,
          totalSessions: a.total_sessions || 0,
          id: a.id,
        })) : [];
        
        const padded = [...loaded];
        while (padded.length < totalSlots) {
          padded.push(null);
        }
        setAgents(padded);
      })
      .catch(() => {
        setAgents(Array(totalSlots).fill(null));
      });
  }, [userName, tenantId, totalSlots]);

  // ── Load user slots (from /api/users, same source as Teams & Roles) ─────────
  const fetchUserSlots = useCallback(async () => {
    if (!userName) return;
    setUserSlotsLoading(true);
    setUserSlotsError(null);
    try {
      const userStr = sessionStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : null;
      const token = userObj?.token || null;
      const res = await fetch(`${SERVER_URL}/api/users`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });
      if (!res.ok) throw new Error('Failed to load users');
      const data = await res.json();
      // Filter users with role 'agent' or 'admin'
      const agentUsers = Array.isArray(data)
        ? data.filter(u => (u.role === 'human_agent' || u.role === 'admin') && u.is_active !== false)
        : [];
      setUserSlots(agentUsers.map(u => ({
        id: u.id,
        name: u.username || u.name || '—',
        email: u.email || '—',
        role: u.role === 'admin' ? 'Admin' : 'Agent',
      })));
    } catch (err) {
      setUserSlotsError(err.message || 'Could not load user slots');
    } finally {
      setUserSlotsLoading(false);
    }
  }, [userName, SERVER_URL]);

  useEffect(() => { fetchUserSlots(); }, [fetchUserSlots]);


  // Payment due enforcement
  useEffect(() => {
    if (!subscription?.renewsAt) { setServiceActive(true); return; }
    const days = daysUntil(subscription.renewsAt);
    if (days !== null && days <= 10) {
      setServiceActive(days > 0);
    } else {
      setServiceActive(true);
    }
  }, [subscription]);

  // Derived values
  const maxAgents = subscription?.maxAiAgents ?? planConfig?.maxAiAgents ?? subscription?.maxAgents ?? planConfig?.maxAgents ?? 0;
  const activeAgentCount = agents.filter(Boolean).length;
  const creditsTotal = subscription?.creditsTotal ?? planConfig?.creditsTotal ?? 0;
  // Cap the displayed monthly credits used at the max monthly credits limit
  const rawCreditsUsed = subscription?.creditsUsed ?? 0;
  const topupCredits      = subscription?.topupCredits      ?? 0;
  const topupCreditsTotal = subscription?.topupCreditsTotal ?? topupCredits; // fallback: treat remaining as total if total unknown
  const creditsRemaining = Math.max(0, creditsTotal - rawCreditsUsed);
  const renewDays = daysUntil(subscription?.renewsAt);

  const plansList = Object.values(PLAN_CONFIGS);
  const currentPlanIdx = planConfig ? plansList.findIndex(p => p.id === planConfig.id) : -1;

  // User slot derived
  const maxUserSlots = subscription?.maxHumanAgents ?? planConfig?.maxHumanAgents ?? subscription?.maxUserSlots ?? planConfig?.maxAgents ?? 0;
  const usedUserSlots = userSlots.length;
  const freeUserSlots = maxUserSlots ? Math.max(0, maxUserSlots - usedUserSlots) : null;

  // ── Invoice filter derived ───────────────────────────────────────────────────
  // Collect unique years from payment history
  const invoiceYears = [...new Set(
    paymentHistory.map(r => new Date(r.paidAt).getFullYear())
  )].sort((a, b) => b - a);

  // Collect months available for selected year (or all if no year selected)
  const invoiceMonths = [...new Set(
    paymentHistory
      .filter(r => !invoiceFilterYear || new Date(r.paidAt).getFullYear() === Number(invoiceFilterYear))
      .map(r => new Date(r.paidAt).getMonth()) // 0-indexed
  )].sort((a, b) => a - b);

  const filteredInvoices = paymentHistory.filter(r => {
    const d = new Date(r.paidAt);
    const yearMatch = !invoiceFilterYear || d.getFullYear() === Number(invoiceFilterYear);
    const monthMatch = !invoiceFilterMonth || d.getMonth() === Number(invoiceFilterMonth);
    return yearMatch && monthMatch;
  });



  // ── Receipt helpers ──────────────────────────────────────────────────────────
  function buildReceiptHtml(record) {
    const date = new Date(record.paidAt).toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'short' });
    const origin = window.location.origin;

    const amount = Number(record.amount);
    const subtotal = Math.round(amount / 1.18);
    const gst = amount - subtotal;
    const addOns = record.addOns;
    const addonCost = ((addOns?.extraAiAgents || 0) + (addOns?.extraHumanAgents || 0)) * 900 + (addOns?.extraCredits?.price || 0);
    const basePrice = subtotal - addonCost;
    const isTopup = record.planType === 'topup';

    let breakdownHtml = '';
    if (isTopup) {
      breakdownHtml = `
        <div class="row"><span class="label">Top-up Pack</span><span class="value">₹${subtotal.toLocaleString('en-IN')}</span></div>
      `;
    } else {
      breakdownHtml = basePrice > 0 ? `
        <div class="row"><span class="label">Base Plan</span><span class="value">₹${basePrice.toLocaleString('en-IN')}</span></div>
      ` : '';
      if (addOns?.extraAiAgents > 0) {
        breakdownHtml += `
        <div class="row" style="padding-left: 12px; border-left: 2px solid #82bbe0; margin-left: 4px;"><span class="label">+ Extra AI Agents (${addOns.extraAiAgents})</span><span class="value">₹${(addOns.extraAiAgents * 900).toLocaleString('en-IN')}</span></div>`;
      }
      if (addOns?.extraHumanAgents > 0) {
        breakdownHtml += `
        <div class="row" style="padding-left: 12px; border-left: 2px solid #82bbe0; margin-left: 4px;"><span class="label">+ Extra Human Agents (${addOns.extraHumanAgents})</span><span class="value">₹${(addOns.extraHumanAgents * 900).toLocaleString('en-IN')}</span></div>`;
      }
      if (addOns?.extraCredits) {
        breakdownHtml += `
        <div class="row" style="padding-left: 12px; border-left: 2px solid #82bbe0; margin-left: 4px;"><span class="label">+ Credit Pack (${addOns.extraCredits.k})</span><span class="value">₹${addOns.extraCredits.price.toLocaleString('en-IN')}</span></div>`;
      }
    }

    return `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Payment Receipt – ${record.paymentId}</title>
<style>
  body{font-family:system-ui,sans-serif;background:#f9fafb;margin:0;padding:40px;color:#111}
  .card{background:#fff;max-width:480px;margin:0 auto;border-radius:16px;padding:40px;box-shadow:0 4px 24px rgba(0,0,0,.08)}
  .logo-wrap{margin-bottom:16px}
  .logo-wrap img{height:60px;object-fit:contain}
  .badge{display:inline-block;background:#e8f5ed;color:#16a34a;font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;margin-bottom:24px}
  .row{display:flex;justify-content:space-between;font-size:13px;margin-bottom:10px}
  .row .label{color:#6b7280}.row .value{font-weight:600}
  .mono{font-family:monospace;font-size:11px;color:#82bbe0}
  hr{border:none;border-top:1px solid #f0f0f0;margin:20px 0}
  .total{font-size:15px;font-weight:800}
  .footer{margin-top:28px;font-size:11px;color:#9ca3af;text-align:center}
  .footer img{height:24px;object-fit:contain;margin-bottom:6px}
</style></head><body>
<div class="card">
  <div class="logo-wrap"><img src="${origin}/logo.png" alt="Logo" /></div>
  <span class="badge">✓ Payment Confirmed</span>
  <h2 style="font-size:20px;margin:0 0 6px">Payment Receipt</h2>
  <p style="color:#6b7280;font-size:13px;margin-bottom:28px">Thank you for your purchase.</p>
  <hr/>
  <div class="row"><span class="label">Customer</span><span class="value">${userName}</span></div>
  <div class="row"><span class="label">Date</span><span class="value">${date}</span></div>
  <div class="row"><span class="label">Payment ID</span><span class="mono">${record.paymentId}</span></div>
  <div class="row"><span class="label">Plan / Pack</span><span class="value">${record.planName}</span></div>
  ${record.validUntil ? `<div class="row"><span class="label">Valid Until</span><span class="value">${new Date(record.validUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>` : ''}
  <hr/>
  ${breakdownHtml}
  <div class="row"><span class="label">GST (18%)</span><span class="value">₹${gst.toLocaleString('en-IN')}</span></div>
  <hr/>
  <div class="row total"><span>Total Paid</span><span>₹${Number(record.amount).toLocaleString('en-IN')}</span></div>
  <div class="row"><span class="label">Status</span><span style="color:#16a34a;font-weight:700">Confirmed</span></div>
  <hr/>
  <div class="footer">Secured by Razorpay · 256-bit SSL encryption<br/>© ${new Date().getFullYear()} All rights reserved.</div>
</div></body></html>`;
  }

  function previewReceipt(record) {
    const url = URL.createObjectURL(new Blob([buildReceiptHtml(record)], { type: 'text/html' }));
    window.open(url, '_blank');
  }

  function downloadReceipt(record) {
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([buildReceiptHtml(record)], { type: 'text/html' })),
      download: `easychat-receipt-${record.paymentId}.html`,
    });
    a.click();
  }

  // ── Loading skeleton state ─────────────────────────────────────────────────
  if (subscription === null) {
    return (
      <div className="bg-transparent w-full space-y-8 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
          <div className="space-y-2">
            <div className="h-6 w-44 bg-zinc-200 rounded-lg" />
            <div className="h-3.5 w-72 bg-zinc-100 rounded" />
          </div>
          <div className="h-9 w-48 bg-zinc-100 rounded-xl" />
        </div>

        {/* Stat Cards Skeleton (5 cards) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/40 space-y-3">
              <div className="flex items-center justify-between">
                <div className="h-3 w-20 bg-zinc-200 rounded" />
                <div className="w-6 h-6 rounded-lg bg-zinc-100" />
              </div>
              <div className="h-6 w-16 bg-zinc-200 rounded" />
              <div className="h-2.5 w-24 bg-zinc-100 rounded" />
            </div>
          ))}
        </div>

        {/* Current Plan Overview Card Skeleton */}
        <div className="p-6 rounded-2xl border border-zinc-200/80 bg-white space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="h-5 w-32 bg-zinc-200 rounded-md" />
                <div className="h-5 w-20 bg-zinc-100 rounded-full" />
              </div>
              <div className="h-3 w-48 bg-zinc-100 rounded" />
            </div>
            <div className="h-9 w-32 bg-zinc-100 rounded-xl" />
          </div>
          
          <div className="space-y-2 pt-2">
            <div className="flex justify-between">
              <div className="h-3 w-28 bg-zinc-200 rounded" />
              <div className="h-3 w-16 bg-zinc-100 rounded" />
            </div>
            <div className="h-2 w-full bg-zinc-100 rounded-full" />
          </div>
        </div>

        {/* Plan Cards Grid Skeleton (4 cards) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="p-6 rounded-2xl border border-zinc-200/80 bg-white space-y-4">
              <div className="h-4 w-24 bg-zinc-200 rounded" />
              <div className="h-7 w-20 bg-zinc-200 rounded" />
              <div className="h-3 w-32 bg-zinc-100 rounded" />
              <div className="space-y-2 pt-4 border-t border-zinc-100">
                <div className="h-3 w-full bg-zinc-100 rounded" />
                <div className="h-3 w-4/5 bg-zinc-100 rounded" />
                <div className="h-3 w-3/4 bg-zinc-100 rounded" />
              </div>
              <div className="h-9 w-full bg-zinc-100 rounded-xl mt-4" />
            </div>
          ))}
        </div>
      </div>
    );
  }



  // ── Full dashboard ───────────────────────────────────────────────────────────
  return (
    <div className="bg-transparent w-full">
      {confirmPlan && (
        <PlanConfirmModal
          plan={confirmPlan}
          billingCycle={billingCycle}
          loading={!!loadingId}
          isCurrent={planConfig?.id === confirmPlan.id}
          onClose={() => setConfirmPlan(null)}
          onConfirm={({ extraAiAgents, extraHumanAgents, extraCredits, total }) => {
            setConfirmPlan(null);
            onSubscriptionPay(
              confirmPlan.id,
              confirmPlan.name,
              typeof confirmPlan.price === 'number' ? confirmPlan.price : 0,
              billingCycle,
              { extraAiAgents, extraHumanAgents, extraCredits, total }
            );
          }}
        />
      )}

      <div className="w-full space-y-12">

        {/* Service suspended banner */}
        {!serviceActive && (
          <div className="flex items-start gap-3 bg-red-50 border-2 border-red-300 rounded-2xl px-5 py-4">
            <ShieldOff size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">  
              <p className="text-sm font-bold text-red-700">Service Suspended</p>
              <p className="text-xs text-red-600 mt-0.5">
                Your subscription expired on <strong>{formatDate(subscription.renewsAt)}</strong>.
                Renew now to restore access to agents, credits, and all features.
              </p>
              <button
                disabled={!!loadingId}
                onClick={() => setConfirmPlan(planConfig || PLAN_CONFIGS['professional'])}
                className="mt-3 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 px-4 py-1.5 rounded-lg transition-all flex items-center gap-1 disabled:opacity-60"
              >
                <RefreshCw size={12} /> Renew Now
              </button>
            </div>
          </div>
        )}

        {/* Header - Minimalist styling */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Billing & Plans</h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Manage your subscription, credits, seats, and view invoices.
            </p>
          </div>
          
          {/* Tab switcher */}
          <div className="flex bg-zinc-100/80 p-1 rounded-xl border border-zinc-200/60 self-start sm:self-auto">
            <button
              onClick={() => {
                setActiveSubTab('overview');
                navigate('/billing');
              }}
              className={`px-4 sm:px-5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 text-center ${
                activeSubTab === 'overview'
                  ? 'bg-white text-zinc-900 shadow-xs'
                  : 'text-zinc-500 hover:text-zinc-900'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => {
                setActiveSubTab('invoices');
                navigate('?tab=invoices');
              }}
              className={`px-4 sm:px-5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 text-center ${
                activeSubTab === 'invoices'
                  ? 'bg-white text-zinc-900 shadow-xs'
                  : 'text-zinc-500 hover:text-zinc-900'
              }`}
            >
              Invoices
            </button>
          </div>
        </div>

        {activeSubTab === 'overview' ? (
          (!subscription.planId || subscription.planName === 'No Plan') ? (
            <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 border border-zinc-200 shadow-xs">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div>
                  <h3 className="font-semibold text-zinc-900 text-sm">Subscription Plan</h3>
                  <p className="text-xs text-zinc-400">Select a plan to pay via Razorpay</p>
                </div>
                <div className="flex items-center gap-1 bg-zinc-100/80 rounded-xl p-1 border border-zinc-200/60">
                  {['monthly', 'yearly'].map(c => (
                    <button
                      key={c}
                      onClick={() => setBillingCycle(c)}
                      className={`px-3 sm:px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${billingCycle === c ? 'bg-white shadow-xs text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'
                        }`}
                    >
                      {c === 'monthly' ? 'Monthly' : (
                        <span className="flex items-center gap-1.5">Yearly <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.2 rounded-full text-[10px] font-bold">-20%</span></span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
                {Object.values(PLAN_CONFIGS).map((plan) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    isCurrent={false}
                    billingCycle={billingCycle}
                    onOpenModal={setConfirmPlan}
                    loading={loadingId === 'plan_' + plan.id}
                    serviceActive={serviceActive}
                  />
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Stat cards — responsive grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 pb-2 pt-1">
              <StatCard
                icon={Users}
                label="Human Agent"
                value={
                  subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise'
                    ? 'Enterprise'
                    : userSlotsLoading
                      ? '…'
                      : userSlotsError
                        ? 'Error'
                        : `${usedUserSlots} / ${maxUserSlots ?? '∞'}`
                }
                sub={
                  subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise'
                    ? 'Unlimited slots'
                    : userSlotsError
                      ? userSlotsError
                      : freeUserSlots !== null
                        ? `${freeUserSlots} slot${freeUserSlots !== 1 ? 's' : ''} available`
                        : 'Unlimited slots'
                }
                loading={userSlotsLoading && !(subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise')}
                highlight={!!userSlotsError}
              />
              <StatCard
                icon={Bot}
                label="AI agents"
                value={
                  subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise'
                    ? 'Enterprise'
                    : `${activeAgentCount} / ${maxAgents ?? '∞'}`
                }
                sub={
                  subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise'
                    ? 'Unlimited slots'
                    : maxAgents ? `${maxAgents - activeAgentCount} slots available` : 'Unlimited slots'
                }
              />
              <StatCard
                icon={Coins}
                label="Credits remaining"
                value={
                  subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise'
                    ? 'Enterprise'
                    : fmt(creditsRemaining + topupCredits)
                }
                sub={
                  subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise'
                    ? 'Unlimited usage'
                    : 'Credits remaining'
                }
              />

              <StatCard
                icon={Wallet}
                label="Daily Budget Left"
                value={
                  todayUsageLoading ? (
                    '…'
                  ) : todayUsage.tokens_today >= todayUsage.daily_token_limit ? (
                    <div>
                      <span className="text-lg sm:text-xl font-semibold text-rose-600 tracking-tight block">
                        {fmt(Math.max(0, todayUsage.daily_token_limit - todayUsage.tokens_today))}
                      </span>
                      <span className="text-[10px] font-bold text-rose-600 block leading-tight mt-0.5">
                        Limit exceeded
                      </span>
                    </div>
                  ) : (
                    fmt(Math.max(0, todayUsage.daily_token_limit - todayUsage.tokens_today))
                  )
                }
                sub={
                  todayUsageLoading ? null : (
                    todayUsage.tokens_today >= todayUsage.daily_token_limit ? (
                      `${fmt(todayUsage.tokens_today)} used`
                    ) : (
                      `${fmt(todayUsage.tokens_today)} used · ${todayUsage.daily_token_limit > 0 ? `${Math.max(0, Math.round(((todayUsage.daily_token_limit - todayUsage.tokens_today) / todayUsage.daily_token_limit) * 100))}% left` : '0% left'}`
                    )
                  )
                }
                loading={todayUsageLoading}
              />


              <StatCard
                icon={Calendar}
                label="Next billing"
                value={
                  subscription.planId?.toLowerCase() === 'enterprise'
                    ? subscription.planName || 'Enterprise'
                    : `₹${fmt(subscription.planPrice)}`
                }
                sub={renewDays !== null ? (renewDays > 0 ? `In ${renewDays} days` : 'Overdue') : ''}
                highlight={renewDays !== null && renewDays <= 10}
              />
            </div>

            {/* Subscription Plans */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 border border-zinc-200 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-zinc-900">Subscription Plan</h3>
                  <p className="text-xs text-zinc-500">Click a plan to pay via Razorpay</p>
                </div>
                <div className="flex items-center gap-1 bg-zinc-50 rounded-xl sm:rounded-2xl p-1 border border-zinc-100 w-full sm:w-auto">
                  {['monthly', 'yearly'].map(c => (
                    <button
                      key={c}
                      onClick={() => setBillingCycle(c)}
                      className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 text-center ${billingCycle === c ? 'bg-white shadow-sm text-zinc-900 border border-zinc-100' : 'text-zinc-400 hover:text-zinc-600'
                        }`}
                    >
                      {c === 'monthly' ? 'Monthly' : (
                        <span className="flex items-center justify-center gap-1">Yearly <span className="text-green-600 text-[10px] font-bold">-20%</span></span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
                {plansList.map((plan, idx) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    isCurrent={subscription?.planId?.toLowerCase() === plan.id.toLowerCase() || planConfig?.id === plan.id}
                    isAbove={planConfig && idx > currentPlanIdx}
                    billingCycle={billingCycle}
                    onOpenModal={setConfirmPlan}
                    loading={loadingId === 'plan_' + plan.id}
                    serviceActive={serviceActive}
                  />
                ))}
              </div>
            </div>

            {/* Agents + Credits */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              {/* Agent Slots — view only */}
              <div className={`bg-white rounded-2xl p-4 sm:p-6 md:p-8 border shadow-xs ${!serviceActive ? 'opacity-50 pointer-events-none select-none' : 'border-zinc-200'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-900">Agent Slots</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {(subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise') ? (
                        "Enterprise — Unlimited Slots"
                      ) : (
                        <>
                          {activeAgentCount} of {maxAgents ?? '∞'} slots used
                          {(() => {
                            const baseMax = planConfig?.maxAiAgents ?? 0;
                            const extras = maxAgents - baseMax;
                            return extras > 0 ? (
                              <span className="ml-1 text-[#82bbe0]">({baseMax} base + {extras} add-on)</span>
                            ) : null;
                          })()}
                        </>
                      )}
                    </p>
                  </div>
                  <span className="self-start sm:self-auto text-[11px] font-medium tracking-wide text-[#82bbe0] bg-[#82bbe0]/10 px-3 py-1 rounded-full">Active Slots</span>
                </div>
                
                <div className="mb-5 mt-3">
                  {(subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise') ? (
                    <div className="flex items-center justify-between py-2 px-3 bg-zinc-50 rounded-lg border border-zinc-100">
                      <span className="text-[11px] font-medium text-zinc-600">Enterprise Capacity</span>
                      <span className="text-[10px] font-bold text-zinc-900 uppercase tracking-wider bg-zinc-200/70 px-2 py-0.5 rounded">Unlimited Allocation</span>
                    </div>
                  ) : (
                    <ProgressBar used={activeAgentCount} total={maxAgents} />
                  )}
                </div>

                <div className="space-y-3 mt-4">
                  {(() => {
                    const activeList = agents.filter(Boolean);
                    const pageSize = 5;
                    const totalPages = Math.ceil(activeList.length / pageSize);
                    const safePage = Math.min(agentPage, Math.max(1, totalPages));
                    const startIndex = (safePage - 1) * pageSize;
                    const paginated = activeList.slice(startIndex, startIndex + pageSize);

                    return (
                      <>
                        {paginated.length > 0 ? (
                          paginated.map((agent, i) => (
                            <AgentSlot
                              key={agent?.id || startIndex + i}
                              agent={agent}
                              index={startIndex + i}
                              disabled={!!loadingId}
                            />
                          ))
                        ) : (
                          <p className="text-xs text-zinc-400 text-center py-4">No active agents</p>
                        )}

                        {totalPages > 1 && (
                          <div className="flex items-center justify-between pt-4 border-t border-zinc-100 mt-4">
                            <span className="text-xs text-zinc-500">
                              Page {safePage} of {totalPages}
                            </span>
                            <div className="flex gap-1">
                              <button
                                disabled={safePage === 1}
                                onClick={() => setAgentPage(p => Math.max(1, p - 1))}
                                className="p-1 rounded-lg border border-zinc-200 text-zinc-600 hover:border-[#82bbe0] hover:text-[#82bbe0] disabled:opacity-40 disabled:hover:border-zinc-200 disabled:hover:text-zinc-600 transition-all"
                              >
                                <ChevronLeft size={16} />
                              </button>
                              <button
                                disabled={safePage === totalPages}
                                onClick={() => setAgentPage(p => Math.min(totalPages, p + 1))}
                                className="p-1 rounded-lg border border-zinc-200 text-zinc-600 hover:border-[#82bbe0] hover:text-[#82bbe0] disabled:opacity-40 disabled:hover:border-zinc-200 disabled:hover:text-zinc-600 transition-all"
                              >
                                <ChevronRight size={16} />
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Buy Credits & Auto Topup Container */}
              <div className={`bg-white rounded-2xl p-4 sm:p-6 md:p-8 border shadow-xs flex flex-col justify-between ${!serviceActive ? 'opacity-50 pointer-events-none select-none' : 'border-zinc-200'}`}>
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                    <div>
                      <h3 className="text-sm font-semibold text-zinc-900">Buy Credits</h3>
                      <p className="text-xs text-zinc-500 mt-0.5">Add query tokens to your agent balance.</p>
                    </div>
                    <span className="self-start sm:self-auto text-[11px] font-medium tracking-wide text-[#82bbe0] bg-[#82bbe0]/10 px-3 py-1 rounded-full">Token Balance</span>
                  </div>

                  {/* Credits progress bars */}
                  <div className="mt-6 space-y-5">
                    {(subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise') ? (
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Plan Limit</span>
                          <span className="text-[10px] font-bold text-zinc-900 bg-zinc-100 px-2 py-0.5 rounded border border-zinc-200/60">Enterprise — Unlimited Tokens</span>
                        </div>
                        <div className="flex items-center justify-between py-2 px-3 bg-zinc-50 rounded-lg border border-zinc-100">
                          <span className="text-[11px] font-medium text-zinc-600">Continuous Token Pipeline</span>
                          <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            No limits enforced
                          </span>
                        </div>
                        <p className="text-[11px] text-zinc-400 mt-2">Enterprise tier includes unlimited automated executions and query queries.</p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Monthly Credits</span>
                            <span className="text-[10px] font-bold text-zinc-700">{fmt(creditsRemaining)} remaining of {fmt(creditsTotal)}</span>
                          </div>
                          <ProgressBar used={creditsRemaining} total={creditsTotal} type="remaining" />
                        </div>

                        {topupCreditsTotal > 0 && (
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Top-up Credits</span>
                              <span className="text-[10px] font-bold text-zinc-700">{fmt(topupCredits)} remaining of {fmt(topupCreditsTotal)}</span>
                            </div>
                            <ProgressBar used={topupCredits} total={topupCreditsTotal} type="remaining" />
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Packs Selector — hidden for Enterprise */}
                  {!(subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise') && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 my-6">
                        {TOPUP_PACKS.map(pack => (
                          <button
                            key={pack.k}
                            onClick={() => setSelectedPack(pack)}
                            className={`p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border text-center transition-all ${
                              selectedPack.k === pack.k
                                ? 'border-[#82bbe0] bg-[#82bbe0]/5 text-[#82bbe0] font-semibold'
                                : 'border-zinc-100 hover:border-zinc-200 text-zinc-600 bg-zinc-50/50 hover:bg-zinc-50'
                            }`}
                          >
                            <span className="block text-sm font-bold">{pack.k}</span>
                            <span className="block text-[10px] text-zinc-400 mt-1 font-bold">₹{pack.price}</span>
                          </button>
                        ))}
                      </div>

                      {/* Buy Button */}
                      <button
                        disabled={!serviceActive || !!loadingId}
                        onClick={() => onCreditPay(selectedPack)}
                        className="w-full py-3 bg-zinc-900 text-white font-bold text-xs uppercase tracking-wider font-semibold rounded-xl hover:bg-zinc-800 transition-all flex items-center justify-center gap-2 shadow-sm disabled:opacity-40"
                      >
                        {loadingId === 'topup_' + selectedPack.k ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                        <span>Buy pack</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* User Slots panel — view only */}
            <div className={`bg-white rounded-2xl p-4 sm:p-6 md:p-8 border shadow-xs ${!serviceActive ? 'opacity-50 pointer-events-none select-none' : 'border-zinc-200'}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-zinc-900">Human Agent</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {(subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise')
                      ? "Enterprise — Unlimited Slots"
                      : userSlotsLoading
                        ? 'Loading…'
                        : `${usedUserSlots} of ${maxUserSlots ?? '∞'} slots used`}
                  </p>
                </div>

              </div>

              {(subscription.planId?.toLowerCase() === 'enterprise' || subscription.planName?.toLowerCase() === 'enterprise') ? (
                <div className="mb-5 mt-3">
                  <div className="flex items-center justify-between py-2 px-3 bg-zinc-50 rounded-lg border border-zinc-100">
                    <span className="text-[11px] font-medium text-zinc-600">Enterprise Seats</span>
                    <span className="text-[10px] font-bold text-zinc-900 uppercase tracking-wider bg-zinc-200/70 px-2 py-0.5 rounded">Unlimited Seats</span>
                  </div>
                </div>
              ) : maxUserSlots > 0 && (
                <div className="mb-5 mt-3">
                  <ProgressBar used={usedUserSlots} total={maxUserSlots} />
                </div>
              )}

              {userSlotsError && !userSlotsLoading && (
                <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-3">
                  <AlertTriangle size={14} />
                  <span>{userSlotsError}</span>
                  <button onClick={fetchUserSlots} className="ml-auto underline font-semibold">Retry</button>
                </div>
              )}

              {userSlotsLoading && (
                <div className="space-y-3 mt-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center gap-3 py-3 px-4 bg-zinc-50/50 rounded-xl border border-zinc-100 animate-pulse">
                      <div className="w-8 h-8 rounded-full bg-zinc-200" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3.5 bg-zinc-200 rounded w-32" />
                        <div className="h-2.5 bg-zinc-100 rounded w-48" />
                      </div>
                      <div className="h-5 w-16 bg-zinc-200 rounded-full" />
                    </div>
                  ))}
                </div>
              )}

              {!userSlotsLoading && (
                <div className="space-y-3 mt-4">
                  {userSlots.length === 0 && !userSlotsError && (
                    <div className="flex flex-col items-center justify-center py-10 text-zinc-400 gap-2">
                      <Users size={32} className="text-zinc-200" />
                      <p className="text-sm font-medium">No human agents provisioned yet</p>
                    </div>
                  )}
                  {userSlots.map(slot => (
                    <UserSlotRow key={slot.id} slot={slot} />
                  ))}
                </div>
              )}

          {!userSlotsLoading && maxUserSlots && usedUserSlots >= maxUserSlots && serviceActive && (
            <p className="text-xs text-amber-600 mt-5 font-bold">
              All user slots used.{' '}
              <button
                onClick={() => setConfirmPlan(PLAN_CONFIGS['enterprise'])}
                className="underline font-semibold hover:text-amber-700 transition-colors"
              >
                Upgrade your plan
              </button>{' '}
              for more seats.
            </p>
          )}
        </div>

        {/* Daily Token Limit Configuration Panel */}
        <div className={`bg-white rounded-2xl p-4 sm:p-6 md:p-8 border shadow-xs border-zinc-200 mt-6 sm:mt-8 ${!serviceActive ? 'opacity-50 pointer-events-none select-none' : ''}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900">Daily Token Limit</h3>
              <p className="text-xs text-zinc-500 mt-0.5">
                Configure your daily token threshold. Automated executions pause when reached.
              </p>
            </div>
            
          </div>

          <div className="mt-4 sm:mt-6 flex flex-col md:flex-row items-stretch md:items-center gap-6 md:gap-8 bg-zinc-50/60 rounded-2xl p-4 sm:p-6 border border-zinc-200/80">
            <div className="flex-1 w-full space-y-4 min-w-0">
              <div className="flex justify-between items-center flex-wrap gap-1">
                <span className="text-xs font-semibold text-zinc-700">Daily Budget Limit:</span>
                <span className="text-sm font-bold text-zinc-900 bg-white px-2.5 py-1 rounded-lg border border-zinc-200 shadow-xs">{fmt(dailyTokenLimit)} Tokens</span>
              </div>
              
              {/* Slider */}
              <div className="py-1">
                <input
                  type="range"
                  min="10000"
                  max="1000000"
                  step="10000"
                  value={dailyTokenLimit}
                  onChange={(e) => setDailyTokenLimit(Number(e.target.value))}
                  className="w-full h-1.5 bg-zinc-200 rounded-lg appearance-none cursor-pointer accent-zinc-900 focus:outline-none"
                />
              </div>
              
              {/* Quick select inputs */}
              <div className="flex flex-wrap gap-2 pt-1 items-center">
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {[50000, 100000, 250000, 500000].map(val => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setDailyTokenLimit(val)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                        dailyTokenLimit === val
                          ? 'border-zinc-900 bg-zinc-900 text-white shadow-xs'
                          : 'border-zinc-200 text-zinc-600 bg-white hover:border-zinc-300'
                      }`}
                    >
                      {fmt(val)}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto pt-2 sm:pt-0">
                  <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">Custom:</span>
                  <input
                    type="number"
                    min="5000"
                    value={dailyTokenLimit}
                    onChange={(e) => setDailyTokenLimit(Math.max(5000, Number(e.target.value)))}
                    className="w-28 px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-900 text-right focus:outline-none focus:border-zinc-900 transition-colors shadow-xs"
                  />
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 w-full md:w-auto">
              <button
                onClick={handleSaveTokenLimit}
                disabled={savingLimit || !serviceActive}
                className="w-full md:w-auto px-6 sm:px-7 py-3 bg-zinc-900 text-white font-semibold text-xs rounded-xl hover:bg-zinc-850 active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-sm disabled:opacity-40"
              >
                {savingLimit ? <Loader2 size={13} className="animate-spin text-zinc-400" /> : null}
                <span>Save Limit</span>
              </button>
            </div>
          </div>
        </div>

        {/* Suspended overlay */}
        {!serviceActive && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex flex-col items-center text-center gap-3">
            <ShieldOff size={24} className="text-red-400" />
            <div>
              <p className="text-sm font-bold text-red-700">All services are suspended</p>
              <p className="text-xs text-red-500 mt-1">Agent slots and credits are frozen. Renew your subscription to restore access.</p>
            </div>
            <button
              disabled={!!loadingId}
              onClick={() => setConfirmPlan(planConfig || PLAN_CONFIGS['professional'])}
              className="px-5 py-2 bg-red-500 text-white text-sm font-bold rounded-xl hover:bg-red-600 transition-all flex items-center gap-2 disabled:opacity-60"
            >
              {loadingId === 'plan_' + (planConfig?.id || 'professional')
                ? <Loader2 size={14} className="animate-spin" />
                : <RefreshCw size={14} />}
              Renew Subscription
            </button>
          </div>
        )}
            </>
          )
        ) : (
          <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-zinc-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-zinc-50/40">
              <div>
                <h3 className="text-sm sm:text-base font-bold text-zinc-900 tracking-tight">Invoice History</h3>
                <p className="text-xs text-zinc-500 mt-0.5">View and download your past subscription receipts and top-ups.</p>
              </div>
              
              {/* Date Filters using standard CustomDropdown */}
              <div className="flex items-center gap-2.5 flex-wrap">
                <CustomDropdown
                  options={[
                    { value: '', label: 'All Years' },
                    ...invoiceYears.map(y => ({ value: String(y), label: String(y) }))
                  ]}
                  value={invoiceFilterYear}
                  onChange={(val) => {
                    setInvoiceFilterYear(val);
                    setInvoiceFilterMonth('');
                  }}
                  className="z-30"
                  buttonClassName="bg-white border border-zinc-200 hover:bg-zinc-50 text-xs font-semibold text-zinc-700 px-3 py-2 rounded-lg shadow-2xs min-w-[110px] justify-between cursor-pointer"
                  renderOption={(opt, isSelected) => (
                    <div className="flex items-center justify-between w-full text-xs">
                      <span className={isSelected ? "font-bold text-zinc-900" : "text-zinc-700"}>{opt.label}</span>
                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 ml-2" />}
                    </div>
                  )}
                />

                <CustomDropdown
                  options={[
                    { value: '', label: 'All Months' },
                    ...invoiceMonths.map(m => ({ value: String(m), label: MONTH_NAMES[m] }))
                  ]}
                  value={invoiceFilterMonth}
                  onChange={(val) => setInvoiceFilterMonth(val)}
                  disabled={!invoiceFilterYear && invoiceMonths.length === 0}
                  className="z-30"
                  buttonClassName="bg-white border border-zinc-200 hover:bg-zinc-50 text-xs font-semibold text-zinc-700 px-3 py-2 rounded-lg shadow-2xs min-w-[120px] justify-between cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  renderOption={(opt, isSelected) => (
                    <div className="flex items-center justify-between w-full text-xs">
                      <span className={isSelected ? "font-bold text-zinc-900" : "text-zinc-700"}>{opt.label}</span>
                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 ml-2" />}
                    </div>
                  )}
                />

                {(invoiceFilterYear || invoiceFilterMonth) && (
                  <button
                    onClick={() => { setInvoiceFilterYear(''); setInvoiceFilterMonth(''); }}
                    className="text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-3 py-2 rounded-lg border border-rose-200/60 shadow-2xs transition-colors cursor-pointer"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Table layout / Empty State */}
            {filteredInvoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] w-full p-8 text-center">
                <FileText size={32} strokeWidth={1.5} className="text-zinc-400 mb-3.5" />
                <h3 className="text-sm sm:text-base font-bold text-zinc-900 tracking-tight mb-1">
                  No invoices found
                </h3>
                <p className="text-xs text-zinc-500 max-w-sm leading-relaxed">
                  There are no payment receipts matching the selected filter criteria.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto w-full">
                <table className="w-full text-left border-collapse min-w-[650px]">
                  <thead className="bg-zinc-50 border-b border-zinc-200 text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                    <tr>
                      <th className="py-3.5 px-5">Billing Item</th>
                      <th className="py-3.5 px-5">Payment ID</th>
                      <th className="py-3.5 px-5">Transaction Date</th>
                      <th className="py-3.5 px-5 text-right">Amount Paid</th>
                      <th className="py-3.5 px-5 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {filteredInvoices.map(record => {
                      let billingLabel = '';
                      let dotColor = '';
                      let displayPlanName = record.planName || '';
                      if (record.planType === 'topup') {
                        billingLabel = 'Credit Top-up';
                        dotColor = 'bg-purple-500';
                      } else {
                        const addOns = record.addOns;
                        const hasAddOns = addOns && (
                          (addOns.extraAiAgents > 0) ||
                          (addOns.extraHumanAgents > 0) ||
                          addOns.extraCredits
                        );
                        const amount = Number(record.amount);
                        const subtotal = Math.round(amount / 1.18);
                        const addonCost = addOns
                          ? ((addOns.extraAiAgents || 0) + (addOns.extraHumanAgents || 0)) * 900 + (addOns.extraCredits?.price || 0)
                          : 0;
                        const basePlanCost = subtotal - addonCost;
                        const planNameLower = String(record.planName).toLowerCase();
                        const isExplicitAddon = planNameLower.includes('extra ai agent') || planNameLower.includes('extra human agent') || planNameLower.includes('addon');
                        
                        if ((hasAddOns || isExplicitAddon) && basePlanCost <= 0) {
                          const parts = [];
                          const targetAddOns = addOns || {};
                          if (targetAddOns.extraAiAgents > 0) {
                            parts.push(`${targetAddOns.extraAiAgents} x Extra AI Agent${targetAddOns.extraAiAgents > 1 ? 's' : ''}`);
                          } else if (planNameLower.includes('extra ai agent')) {
                            const match = planNameLower.match(/(\d+)\s*(?:x\s*)?extra ai agent/);
                            parts.push(match ? `${match[1]} x Extra AI Agent${parseInt(match[1]) > 1 ? 's' : ''}` : "Extra AI Agent");
                          }

                          if (targetAddOns.extraHumanAgents > 0) {
                            parts.push(`${targetAddOns.extraHumanAgents} x Extra Human Agent${targetAddOns.extraHumanAgents > 1 ? 's' : ''}`);
                          } else if (planNameLower.includes('extra human agent')) {
                            const match = planNameLower.match(/(\d+)\s*(?:x\s*)?extra human agent/);
                            parts.push(match ? `${match[1]} x Extra Human Agent${parseInt(match[1]) > 1 ? 's' : ''}` : "Extra Human Agent");
                          }

                          if (targetAddOns.extraCredits) {
                            parts.push(`Credit Pack (${targetAddOns.extraCredits.k})`);
                          }

                          displayPlanName = parts.join(" + ") || record.planName;
                          billingLabel = 'Add-on';
                          dotColor = 'bg-amber-500';
                        } else if ((hasAddOns || isExplicitAddon) && basePlanCost > 0) {
                          billingLabel = 'Subscription + Add-ons';
                          dotColor = 'bg-emerald-500';
                        } else {
                          billingLabel = 'Subscription';
                          dotColor = 'bg-emerald-500';
                        }
                      }
                      return (
                        <tr key={record.paymentId} className="hover:bg-zinc-50/60 transition-colors">
                          <td className="py-3.5 px-5">
                            <div className="flex items-center gap-2.5">
                              <div className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
                              <div className="min-w-0">
                                <span className="text-xs font-bold text-zinc-900 block truncate max-w-[220px] sm:max-w-none">{displayPlanName || record.planName}</span>
                                <span className="block text-[10px] text-zinc-400 font-medium capitalize mt-0.5">{billingLabel}</span>
                              </div>
                            </div>
                          </td>
                          <td className="py-3.5 px-5 text-xs font-mono text-zinc-500 whitespace-nowrap">{record.paymentId}</td>
                          <td className="py-3.5 px-5 text-xs text-zinc-600 whitespace-nowrap">
                            {new Date(record.paidAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="py-3.5 px-5 text-xs font-bold text-zinc-900 text-right whitespace-nowrap">
                            ₹{Number(record.amount).toLocaleString('en-IN')}
                          </td>
                          <td className="py-3.5 px-5">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => previewReceipt(record)}
                                title="Preview Receipt"
                                className="px-2.5 py-1.5 rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 transition-colors flex items-center gap-1.5 text-xs font-semibold bg-white shadow-2xs cursor-pointer"
                              >
                                <Eye size={13} />
                                <span>Preview</span>
                              </button>
                              <button
                                onClick={() => downloadReceipt(record)}
                                title="Download PDF"
                                className="px-2.5 py-1.5 rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 transition-colors flex items-center gap-1.5 text-xs font-semibold bg-white shadow-2xs cursor-pointer"
                              >
                                <Download size={13} />
                                <span>Download</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
